import { Card, Button } from "@/components/ui";
import useDashboard from "./useDashboard";
import { CountUp, FormSelect, PhotoSlider } from "@/components";
import {
  HardHat,
  Settings,
  Calendar,
  ChartColumn,
  FileCheck,
  User,
  Image,
  X,
  Check,
  MousePointer2,
} from "lucide-react";
import {
  TITLE,
  VALUE,
  SUBTITLE,
  TYPE,
  PROGRESS,
  CATEGORY,
  DELAYED,
  SAFETY,
  QUALITY,
  TOTAL_PHOTOS,
  PENDING_APPROVAL,
  APPROVED,
  REJECTED,
  PROJECT_LIST,
  bgMap,
} from "./consts";
import { iconMap } from "./iconMap";
import {
  getProjectStatusClassName,
  getProjectHealthLabel,
} from "@/lib/utils";

const PmDashboard = () => {

  const {
    loggedInUserData,
    statsCards,
    safetyCompliance,
    qualityControl,
    overallProgress,
    categoryPerformance,
    projectList,
    clientSharedPhotos,
    selectedProject,
    keys,
    requiredFields,
    siteManagerLastUpdate,
    form,
    upcomingEvents,
    scheduleVariance,
    closureReadiness,
    isPhotoSliderOpen,
    setIsPhotoSliderOpen,
    selectedCategory,
    setSelectedCategory,
    handleMajorCountClick,
    selectedPhotos,
    handleReviewPhoto,
    projectPlanVersion
  } = useDashboard();

  const { errors } = form.formState;


  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg text-gray-600">
          Hello, {loggedInUserData?.fullName || ""}
        </h1>
        <h2 className="text-2xl font-semibold">Project Manager</h2>
      </div>

      {/* Stats Cards */}
      <Card className="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statsCards?.map((card: any, index: number) => (
            <div
              key={index}
              className={`p-4 rounded-xl border flex justify-between items-start ${
                bgMap[card[TYPE]]
              }`}
            >
              <div>
                <p className="text-sm text-gray-600">{card[TITLE]}</p>
                <p className="text-2xl font-bold mt-1">
                  <CountUp value={card[VALUE]} />
                </p>
                <p className="text-xs text-gray-500 mt-1">{card[SUBTITLE]}</p>
              </div>
              {iconMap[card[TYPE]]}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 bg-gray-50">
        <Card className="p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <MousePointer2 className="w-6 h-6 text-yellow-500 mt-1" />
              <div>
                <h3 className="text-md font-semibold mb-2">Select Project</h3>
                <p className="text-sm text-gray-500">
                  Full project execution visibility
                </p>
              </div>
            </div>

            <div className="w-full md:w-[280px]">
              <FormSelect
                key={keys.projectList}
                name="projectList"
                control={form.control}
                label="Select Project"
                placeholder="Please select"
                error={errors?.projectList?.message}
                isRequired={requiredFields.includes(PROJECT_LIST)}
                options={(projectList?.data || []).map((project: any) => ({
                  label: project?.name,
                  value: String(project?.projectId),
                }))}
              />
            </div>
          </div>
        </Card>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 px-3">
          <div>
            <h3 className="text-md font-semibold">
              {selectedProject?.name || ""}
            </h3>

            <p className="text-sm text-gray-500 mt-1">
              Status:{" "}
              <span
                className={getProjectStatusClassName(selectedProject?.status)}
              >
                {getProjectHealthLabel(selectedProject?.status)}
              </span>{" "}
              • Plan Version {projectPlanVersion || "N/A"} • Planned End: {scheduleVariance?.plannedEndDate ? new Date(scheduleVariance.plannedEndDate).toLocaleDateString("en-GB", { day: '2-digit', month: 'short', year: 'numeric' }) : "N/A"}
            </p>
          </div>
        </div>
        <Card className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-0">
            {overallProgress?.map((card: any, index: number) => (
              <div
                key={index}
                className={`p-4 rounded-xl border flex justify-between items-start ${
                  bgMap[card[TYPE]]
                }`}
              >
                <div>
                  <p className="text-sm text-gray-600">{card[TITLE]}</p>
                  <p className="text-2xl font-semibold mt-1">
                    <CountUp value={card[VALUE]} />
                  </p>
                </div>
                {iconMap[card[TYPE]]}
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ">
            <h3 className="text-md font-semibold">Category-wise Performance</h3>
            <div className="mt-0">
              <Button onClick={handleMajorCountClick}>View Detail</Button>
            </div>
          </div>

          <div className="space-y-3">
            {categoryPerformance?.map((item: any, index: number) => (
              <div
                key={index}
                className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 rounded-xl bg-primary-100 p-4"
              >
                <div>
                  <p className="font-medium text-gray-800">{item[CATEGORY]}</p>

                  <p className="text-sm text-gray-600">
                    Progress: {item[PROGRESS]}%
                  </p>

                  <p className="text-xs text-gray-500 mt-3">{item[SUBTITLE]}</p>
                </div>

                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <span
                    className={`px-3 py-1 text-xs rounded-full border ${
                      item[DELAYED] > 0
                        ? "bg-red-600 text-white border-red-200"
                        : "text-gray-600 border-gray-200"
                    }`}
                  >
                    {item[DELAYED]} Delayed
                  </span>
                  <span
                    className={`px-3 py-1 text-xs rounded-full border ${
                      item[SAFETY] > 0
                        ? "bg-red-600 text-white border-red-200"
                        : "text-gray-600 border-gray-200"
                    }`}
                  >
                    {item[SAFETY]} Safety
                  </span>
                  <span
                    className={`px-3 py-1 text-xs rounded-full border ${
                      item[QUALITY] > 0
                        ? "bg-red-600 text-white border-red-200"
                        : "text-gray-600 border-gray-200"
                    }`}
                  >
                    {item[QUALITY]} Quality
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-6 h-6 text-yellow-500" />
              <h3 className="text-md font-semibold">Upcoming Events:</h3>
            </div>
            <div className="space-y-4 mt-2">
              {upcomingEvents?.length > 0 ? (
                upcomingEvents.map((event: any, index: number) => (
                  <div
                    key={index}
                    className="flex justify-between items-center border-b border-gray-100 last:border-0 pb-2 last:pb-0"
                  >
                    <p className="text-sm text-gray-600">
                      Your Next event{" "}
                      <span className="font-semibold">{event.taskName}</span> (
                      {event.categoryName}){" "}
                      {event.eventType === "TASK_END" ? "Completion" : "Start"}{" "}
                      is on{" "}
                      <span className="font-semibold">
                        {new Date(event.eventDate).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </span>
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 italic">
                  No upcoming events found for this project
                </p>
              )}
            </div>
          </Card>
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <ChartColumn className="w-6 h-6 text-yellow-500" />
              <h3 className="text-md font-semibold">Schedule Variance</h3>
            </div>

            <div className="flex justify-between items-center mb-1">
              <p className="text-sm text-gray-600">
                Current Variance:
                <span
                  className={`font-bold ml-1 ${
                    scheduleVariance?.varianceDays > 0
                      ? "text-red-600"
                      : "text-green-600"
                  }`}
                >
                  {scheduleVariance?.varianceDays || 0} Days
                </span>
              </p>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Auto-calculated from Planned (
              {scheduleVariance?.plannedEndDate
                ? new Date(scheduleVariance?.plannedEndDate).toLocaleDateString(
                    "en-GB"
                  )
                : "N/A"}
              ) vs Actual dates
            </p>
          </Card>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="p-5">
                    <div className="flex items-center gap-2 mb-1">
                      <HardHat className="w-6 h-6 text-yellow-500" />
                      <h3 className="text-md font-semibold">Safety Audit Compliance</h3>
                    </div>
        
                    <div 
                      onClick={(safetyCompliance?.major ?? 0) > 0 ? handleMajorCountClick : undefined}
                      className={`flex justify-between items-center mb-1 rounded-md transition-colors ${
                        (safetyCompliance?.major ?? 0) > 0 
                          ? "cursor-pointer group hover:bg-gray-50 text-gray-900" 
                          : "cursor-default text-gray-600"
                      }`}
                    >
                      <p className="text-sm">Major Non-Compliance</p>
                      <span 
                        className={`border text-xs px-2 py-1 rounded-full transition-colors ${
                          (safetyCompliance?.major ?? 0) > 0 
                            ? "bg-red-500 text-white group-hover:bg-red-600" 
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {safetyCompliance.major}
                      </span>
                    </div>
        
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-gray-600">Minor Non-Compliance</p>
                      <span className="border text-xs px-2 py-1 rounded-full">
                        {safetyCompliance.minor}
                      </span>
                    </div>
        
                    <Button
                      disabled
                      className="w-full justify-start bg-gray-200 text-gray-800 hover:bg-gray-100 cursor-not-allowed rounded-xl h-11">
                      Tasks locked until Compliance = Pass
                    </Button>
                  </Card>
                  <Card className="p-5">
                    <div className="flex items-center gap-2 mb-1">
                      <Settings className="w-6 h-6 text-yellow-500" />
                      <h3 className="text-md font-semibold">Quality Control</h3>
                    </div>
        
                    <div className="flex justify-between items-center mb-1">
                      <p className="text-sm text-gray-600">Rework Required</p>
                      <span className="border text-xs px-2 py-1 rounded-full">
                        {qualityControl.rework}
                      </span>
                    </div>
        
                    <div 
                      onClick={(qualityControl?.majorFailure ?? 0) > 0 ? handleMajorCountClick : undefined}
                      className={`flex justify-between items-center mb-1 rounded-md transition-colors ${
                        (qualityControl?.majorFailure ?? 0) > 0 
                        ? "cursor-pointer group hover:bg-gray-50 text-gray-900" 
                        : "cursor-default text-gray-600"
                      }`}
                    >
                      <p className="text-sm">Major Failure</p>
                      <span 
                        className={`border text-xs px-2 py-1 rounded-full transition-colors ${
                          (qualityControl?.majorFailure ?? 0) > 0 
                            ? "bg-red-500 text-white group-hover:bg-red-600" 
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {qualityControl?.majorFailure}
                      </span>
                    </div>
        
        
                    <Button
                      disabled
                      className="w-full justify-start bg-gray-200 text-gray-800 hover:bg-gray-100 cursor-not-allowed rounded-xl h-11">
                      Task Locked until Acceptable
                    </Button>
                  </Card>
                </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <User className="w-6 h-6 text-yellow-500" />
              <h3 className="text-md font-semibold">
                Site Manager Daily Monitoring
              </h3>
            </div>

            {/* <div className="flex justify-between items-center mb-1"> */}
            <p className="text-sm text-gray-600">
              Last Update: {siteManagerLastUpdate}
            </p>
            {/* </div> */}
          </Card>

          <Card className="p-6 border border-gray-200">
            <div className="flex items-center gap-2 mb-1">
              <FileCheck className="w-6 h-6 text-yellow-500" />
              <h3 className="text-md font-semibold">Closure Readiness</h3>
            </div>
            <div className="space-y-3 mb-6 mt-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">All Tasks 100% Complete</p>
                {closureReadiness?.allTasksComplete ? (
                  <Check className="w-5 h-5 text-green-600" />
                ) : (
                  <X className="w-5 h-5 text-red-600" />
                )}
              </div>

              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">All Safety Audits Closed</p>
                {closureReadiness?.allSafetyAuditsClosed ? (
                  <Check className="w-5 h-5 text-green-600" />
                ) : (
                  <X className="w-5 h-5 text-red-600" />
                )}
              </div>

              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">All Quality Audits Closed</p>
                {closureReadiness?.allQualityAuditsClosed ? (
                  <Check className="w-5 h-5 text-green-600" />
                ) : (
                  <X className="w-5 h-5 text-red-600" />
                )}
              </div>
            </div>

            <Button
              disabled={!closureReadiness?.canClose}
              className={`w-full justify-start rounded-xl h-11 transition-all ${
                closureReadiness?.canClose
                  ? "bg-primary text-white hover:bg-primary-hover shadow-md"
                  : "bg-gray-200 text-gray-800 hover:bg-gray-100 cursor-not-allowed"
              }`}
            >
              {closureReadiness?.canClose
                ? "Proceed with Project Closure"
                : "Close Project (Disabled Until All Checks Pass)"}
            </Button>
          </Card>
        </div>
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Image className="w-5 h-5 text-yellow-500" />
            <h3 className="text-md font-semibold">Client Shared Photos</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {clientSharedPhotos?.map((item: any, index: number) => (
              <div
                key={index}
                className="bg-[#3a3629] rounded-md p-4 min-h-[120px]"
              >
                <p className="text-yellow-400 text-sm font-medium mb-3">
                  {item[CATEGORY]}
                </p>

                <div className="space-y-1 text-xs">
                  <div
                    className="flex justify-between text-white gap-3 rounded transition-colors"
                  >
                    <span>Total Photos</span>
                    <span onClick={() => {
                      setSelectedCategory(item[CATEGORY]);
                      setIsPhotoSliderOpen(true);
                    }} className={`text-yellow-400 ${item[TOTAL_PHOTOS] > 0 ? "underline cursor-pointer" : "pointer-events-none"}`}>
                      : {item[TOTAL_PHOTOS]}
                    </span>
                  </div>

                  <div className="flex justify-between text-white gap-3">
                    <span>Pending Approval</span>
                    <span className="text-yellow-400 shrink-0">
                      : {item[PENDING_APPROVAL]}
                    </span>
                  </div>

                  <div className="flex justify-between text-white gap-3">
                    <span>Approved</span>
                    <span className="text-yellow-400 shrink-0">
                      : {item[APPROVED]}
                    </span>
                  </div>

                  <div className="flex justify-between text-white gap-3">
                    <span>Rejected</span>
                    <span className="text-yellow-400 shrink-0">
                      : {item[REJECTED]}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Button
            disabled
            className="w-full sm:w-fit justify-start bg-gray-200 text-gray-500 hover:bg-gray-200 cursor-not-allowed rounded-xl h-11"
          >
            Tagged Start & Completion images visible to Client Dashboard
          </Button>
        </Card>
      </Card>
      <PhotoSlider
        isOpen={isPhotoSliderOpen}
        onClose={() => setIsPhotoSliderOpen(false)}
        category={selectedCategory}
        photos={selectedPhotos}
        onReview={handleReviewPhoto}
      />
    </div>
  );
};

export default PmDashboard;